import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  BookingConfirmation,
  BookingIntentResponse,
  BookingPaymentSession,
  Faq,
  FeaturedHotelCard,
  FeaturedRoomsResponse,
  MemberProfile,
  MemberRegisterRequest,
  RoomOffer,
  SecretsEvent,
  SecretsGalleryImage,
  SecretsNewsArticle,
  Slide,
} from '@secretsoverflow/types';
import { formatMoney } from '@secretsoverflow/utils';
import { api, ApiClientError } from '../lib/api';
import { useAuth } from '../lib/useAuth';

// Renders a real Zemez-template HTML page (served from /theme/pages) with the
// template's own CSS/JS, so the public site IS the template rather than a
// lookalike. The template is jQuery/Swiper-driven, so we inject the markup and
// then run its init script against that DOM.

const THEME_CSS = [
  '/theme/css/bootstrap.css',
  '/theme/css/style.css',
  '/theme/css/fonts.css',
  '/brand/overrides.css',
];

// The generated pages already carry extension-less, root-relative routes
// (scripts/generate-pages.mjs rewrites the template's .html links), so an
// internal link is any "/path" that isn't pointing at a file.
const isInternalRoute = (href: string): boolean =>
  href.startsWith('/') && !/\.[a-z0-9]+$/i.test(href);

// SecretsFL is the source of the Gallery/News/Events content, so those pages
// declare a rel=canonical pointing at the SecretsFL section to avoid Google
// flagging duplicate content. Other pages get no managed canonical.
const CANONICAL_BY_SRC: Record<string, string> = {
  'gallery.html': 'https://www.secretsfl.com/galleries',
  'news.html': 'https://www.secretsfl.com/news',
  'events.html': 'https://www.secretsfl.com/events',
};

function setCanonical(src: string): void {
  const match = Object.entries(CANONICAL_BY_SRC).find(([key]) => src.includes(key));
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"][data-managed]');
  if (!match) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    link.dataset.managed = 'true';
    document.head.appendChild(link);
  }
  link.href = match[1];
}

function ensureThemeCss(): void {
  for (const href of THEME_CSS) {
    if (!document.querySelector(`link[data-theme-css="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.themeCss = href;
      document.head.appendChild(link);
    }
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

// Parse a date-picker value (native or "24 July, 2026") into YYYY-MM-DD.
function toIsoDate(value: string): string | null {
  if (!value) return null;
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct.toISOString().slice(0, 10);
  const m = value.match(/(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})/);
  if (m) {
    const d = new Date(`${m[2]} ${m[1]}, ${m[3]}`);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return null;
}

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

function message(text: string): string {
  return `<div class="container"><p class="text-center">${escapeHtml(text)}</p></div>`;
}

type SortKey = 'distance' | 'price' | 'rating';

// The vendor reports distance in kilometers; American readers want miles.
const miles = (km: number): number => Math.round(km * 0.621371 * 10) / 10;

interface HotelGroup {
  hotel: RoomOffer['hotel'];
  /** Every room this hotel has open, cheapest first. */
  rooms: RoomOffer[];
}

// A search comes back as a flat list of rates, and a single property can easily
// account for a dozen of them. Listed raw, one hotel swamps the page and it
// reads as no choice at all. Collapse to one card per property instead.
function groupByHotel(offers: RoomOffer[]): HotelGroup[] {
  const groups = new Map<string, HotelGroup>();
  for (const offer of offers) {
    const group = groups.get(offer.hotel.id);
    if (group) group.rooms.push(offer);
    else groups.set(offer.hotel.id, { hotel: offer.hotel, rooms: [offer] });
  }
  for (const group of groups.values()) {
    group.rooms.sort((a, b) => a.totalPriceCents - b.totalPriceCents);
  }
  return [...groups.values()];
}

function sortGroups(groups: HotelGroup[], key: SortKey): HotelGroup[] {
  const cheapest = (g: HotelGroup) => g.rooms[0]?.totalPriceCents ?? Number.MAX_SAFE_INTEGER;
  return [...groups].sort((a, b) => {
    if (key === 'price') return cheapest(a) - cheapest(b);
    if (key === 'rating') {
      return (
        (b.hotel.starRating ?? 0) - (a.hotel.starRating ?? 0) ||
        a.hotel.distanceKm - b.hotel.distanceKm
      );
    }
    return a.hotel.distanceKm - b.hotel.distanceKm;
  });
}

// Vendor rate ids are long opaque tokens, 458 to 3621 characters in one live
// search, so putting them in a markup attribute would add megabytes of DOM for
// data the page is already holding in memory. The button carries a short key
// into that list instead, and the real id is looked up when it is clicked.
const offerKeys = new WeakMap<RoomOffer, string>();

// Reserving opens our own checkout rather than throwing the visitor straight at
// the vendor. That one step is where the booking gets written down and where we
// get a name to attach to it; the vendor handoff still happens, just after.
function reserveButton(o: RoomOffer, size = 'button-sm'): string {
  return `<button type="button" class="button button-primary ${size}" data-book="${offerKeys.get(o) ?? ''}">Reserve</button>`;
}

function roomRow(o: RoomOffer): string {
  // Always state the cancellation stance: showing "refundable" only when true
  // left a non-refundable rate looking like it simply hadn't said, so a guest
  // couldn't tell the two apart.
  const facts = [`sleeps ${o.maxGuests}`, o.refundable ? 'Free cancellation' : 'Non-refundable']
    .join(' &middot; ');
  return `<li class="room-row">
    <div>
      <span class="room-row-type">${escapeHtml(o.roomType)}</span>
      <span class="fs-sm room-row-facts">${facts}</span>
    </div>
    <div class="room-row-price">
      <strong>${formatMoney(o.totalPriceCents, o.currency)}</strong>
      <span class="fs-sm room-row-facts">${formatMoney(o.nightlyPriceCents, o.currency)}/night</span>
    </div>
    ${reserveButton(o)}
  </li>`;
}

function hotelGroupCard(g: HotelGroup): string {
  const lead = g.rooms[0];
  if (!lead) return '';
  const stars = g.hotel.starRating ? '★'.repeat(Math.max(0, Math.round(g.hotel.starRating))) : '';
  const place = [g.hotel.city, g.hotel.region].filter(Boolean).join(', ');
  const facts = [place, `${miles(g.hotel.distanceKm)} miles from the resort`]
    .filter(Boolean)
    .join(' &middot; ');
  const others = g.rooms.slice(1);
  const moreRooms = others.length
    ? `<details class="hotel-rooms">
        <summary>${others.length} other room${others.length === 1 ? '' : 's'} at this hotel</summary>
        <ul class="room-list">${others.map(roomRow).join('')}</ul>
      </details>`
    : '';
  return `<div class="col-12 col-xl-6">
    <article class="hotel-result">
      <figure class="hotel-result-image"><img src="${escapeHtml(g.hotel.thumbnailUrl || '/theme/images/home-01-570x370.jpg')}" alt="${escapeHtml(g.hotel.name)}" width="570" height="370"/></figure>
      <div class="hotel-result-body">
        <h5 class="hotel-result-title">${escapeHtml(g.hotel.name)}${stars ? ` <span class="hotel-result-stars">${stars}</span>` : ''}</h5>
        <p class="fs-sm hotel-card-meta">${facts}</p>
        <ul class="room-list">${roomRow(lead)}</ul>
        ${moreRooms}
      </div>
    </article>
  </div>`;
}

// --- Checkout ---------------------------------------------------------------
// Between picking a room and going off to the vendor sits one step: who is
// booking. It is the only point in the journey where a search turns into a
// person, so it offers signing in, creating an account, or carrying on as a
// guest. Nobody is made to register to get a room, but they are asked.
//
// The booking row is written before any of that, on the way in, so a checkout
// somebody walks away from still leaves us the search that led to it.

interface CheckoutDeps {
  memberLogin: (email: string, password: string) => Promise<void>;
  memberRegister: (input: MemberRegisterRequest) => Promise<{ pendingReason: string | null }>;
  /** Read lazily: the visitor may sign in during checkout. */
  getMember: () => MemberProfile | null;
}

type CheckoutMode = 'signin' | 'guest';

// Zod rejections come back as a flat "Invalid request" with the real problem
// buried in `details`, so surface the first field message instead.
function errorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiClientError)) return fallback;
  const first = Object.values(err.details ?? {})[0]?.[0];
  if (err.code === 'validation_error' && first) return first;
  return err.message || fallback;
}

function checkoutSummary(b: BookingIntentResponse): string {
  const fact = (label: string, value: string) =>
    `<li><span>${label}</span><strong>${escapeHtml(value)}</strong></li>`;
  return `<div class="checkout-summary">
    <h4 class="checkout-hotel">${escapeHtml(b.hotelName)}</h4>
    <p class="po-muted">${escapeHtml(b.roomType)}</p>
    <ul class="checkout-facts">
      ${fact('Check in', formatDate(b.checkIn) || b.checkIn)}
      ${fact('Check out', formatDate(b.checkOut) || b.checkOut)}
      ${fact('Stay', `${b.nights} night${b.nights === 1 ? '' : 's'}, ${b.guests} guest${b.guests === 1 ? '' : 's'}`)}
      ${fact(`${formatMoney(b.nightlyPriceCents, b.currency)} per night`, `${formatMoney(b.totalPriceCents, b.currency)} total`)}
    </ul>
    ${b.refundable
      ? '<p class="fs-sm checkout-refundable">Free cancellation on this rate.</p>'
      : '<p class="fs-sm checkout-refundable">This rate is non-refundable &mdash; it cannot be cancelled once booked.</p>'}
  </div>`;
}

function field(id: string, label: string, type: string, opts: { value?: string; autocomplete?: string; required?: boolean } = {}): string {
  return `<div class="member-field">
    <label class="form-label-outside" for="${id}">${escapeHtml(label)}</label>
    <input class="form-input" id="${id}" name="${id}" type="${type}"
      autocomplete="${opts.autocomplete ?? 'off'}"${opts.required === false ? '' : ' required'}
      value="${escapeHtml(opts.value ?? '')}"/>
  </div>`;
}

/**
 * The payment step. The card form is rendered by the vendor's own SDK against
 * their Stripe account, so no card detail ever reaches this page or our API.
 */
const PAYMENT_SDK_URL = 'https://payment-wrapper.liteapi.travel/dist/liteAPIPayment.js?v=a1';

interface LiteApiPaymentCtor {
  new (config: Record<string, unknown>): { handlePayment: () => void };
}

function payPanel(busy: boolean, error: string | null): string {
  const notice = error
    ? `<div class="member-notice member-notice-error">${escapeHtml(error)}</div>`
    : '<div class="member-notice member-notice-info">We have your details. Pay to hold the room and it is yours.</div>';
  return `${notice}
    ${busy ? '<p class="text-center po-muted">Holding your room…</p>' : ''}
    <div id="checkout-payment" class="checkout-payment"></div>
    <p class="member-hint">Payment is taken by the hotel provider, not by us. Your card details never touch this site.</p>`;
}

/**
 * Hand the held room over to the vendor's payment form.
 *
 * Their SDK renders Stripe Elements into the target element and, on success,
 * sends the browser to `returnUrl`. We come back to the booking page carrying
 * the booking id, and the page finishes by asking our API to book it.
 */
async function mountPaymentForm(session: BookingPaymentSession): Promise<void> {
  const w = window as unknown as { LiteAPIPayment?: LiteApiPaymentCtor };
  if (!w.LiteAPIPayment) await loadScript(PAYMENT_SDK_URL);
  if (!w.LiteAPIPayment) throw new Error('The payment form could not be loaded.');

  const returnUrl = `${window.location.origin}/book?booking=${encodeURIComponent(session.bookingId)}`;
  new w.LiteAPIPayment({
    publicKey: session.environment,
    secretKey: session.clientSecret,
    returnUrl,
    targetElement: '#checkout-payment',
    appearance: { theme: 'flat' },
    options: { business: { name: 'Secrets Hideaway' } },
  }).handlePayment();
}

/** The receipt, once the room is actually booked. */
function confirmedPanel(c: BookingConfirmation): string {
  const code = c.confirmationCode || c.vendorBookingId;
  return `<div class="member-notice member-notice-info"><strong>You are booked.</strong> ${escapeHtml(c.hotelName)}, ${escapeHtml(formatDate(c.checkIn) || c.checkIn)} to ${escapeHtml(formatDate(c.checkOut) || c.checkOut)}.</div>
    ${code ? `<div class="checkout-code"><span>Confirmation</span><strong>${escapeHtml(code)}</strong></div>` : ''}
    <p class="member-hint">Keep that reference. Quote it to the hotel or to us if you need to change anything.</p>
    <a class="button button-default-outline button-block" href="/book">Book another room</a>`;
}

/**
 * Draw the checkout for a booking that has already been recorded. Re-renders in
 * place on tab switches and after the identity step, so the wiring is redone
 * each pass.
 */
function renderCheckout(
  el: HTMLElement,
  initialBooking: BookingIntentResponse,
  deps: CheckoutDeps,
  back: () => void,
): void {
  let mode: CheckoutMode = 'signin';
  let booking = initialBooking;
  let identified = false;
  /** Set when a signed-in visitor asks to book under different details. */
  let asGuest = false;
  /** Why the room could not be held, when that is what went wrong. */
  let paymentError: string | null = null;
  /** Set when the room we actually held is not the one they picked. */
  let priceChange: string | null = null;
  let notice: { kind: 'error' | 'info'; text: string } | null = null;
  /** Set when a new account was made but cannot be used yet. */
  let accountNote: string | null = null;
  let busy = false;

  // Each pass rebuilds the panel's markup, which would otherwise empty every
  // field the visitor had filled in. Losing a typed-out form to a rejected
  // password is the kind of thing that ends a booking, so carry the values
  // across by id.
  // Accumulated across the whole checkout rather than snapshotted per pass.
  // Switching tabs tears one form down and builds the other, so a per-pass
  // snapshot only ever carries the tab you are already looking at, and stepping
  // over to "continue as guest" would still lose what you had typed.
  const carried = new Map<string, string | boolean>();

  const snapshot = (): void => {
    el.querySelectorAll<HTMLInputElement>('input[id]').forEach((i) => {
      carried.set(i.id, i.type === 'checkbox' ? i.checked : i.value);
    });
  };

  const restore = (): void => {
    el.querySelectorAll<HTMLInputElement>('input[id]').forEach((i) => {
      const saved = carried.get(i.id);
      if (saved === undefined) return;
      if (typeof saved === 'boolean') i.checked = saved;
      else i.value = saved;
    });
    // The two tabs ask for the same address under different names. Someone who
    // starts at the sign-in tab and gives up should not type it again. Only
    // ever fills a field that is still empty, so it cannot clobber an edit.
    const email = el.querySelector<HTMLInputElement>('#guestEmail, #bookEmail');
    if (email && !email.value) {
      const other = carried.get(email.id === 'guestEmail' ? 'bookEmail' : 'guestEmail');
      if (typeof other === 'string') email.value = other;
    }
    // The opt-in drives both the panel and whether its fields are demanded, so
    // replay it rather than leaving the restored tick with nothing behind it.
    const optIn = el.querySelector<HTMLInputElement>('#guestCreateAccount');
    optIn?.dispatchEvent(new Event('change'));
  };

  const draw = (): void => {
    snapshot();
    const member = deps.getMember();
    const noticeHtml = notice
      ? `<div class="member-notice member-notice-${notice.kind}">${escapeHtml(notice.text)}</div>`
      : '';

    let body: string;
    if (identified) {
      const note = accountNote
        ? `<div class="member-notice member-notice-info">Your account has been created. ${escapeHtml(accountNote)}</div>`
        : '';
      const changed = priceChange
        ? `<div class="member-notice member-notice-error">${escapeHtml(priceChange)}</div>`
        : '';
      body = `${note}${changed}${payPanel(busy, paymentError)}`;
    } else if (member && !asGuest) {
      // Already signed in: confirm rather than asking them to type it again.
      const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.username;
      body = `<p>Booking as <strong>${escapeHtml(name)}</strong> (${escapeHtml(member.email)}).</p>
        <form data-checkout="member">
          ${field('bookPhone', 'Phone number (optional)', 'tel', { autocomplete: 'tel', required: false })}
          <button class="button button-primary button-block" type="submit"${busy ? ' disabled' : ''}>${busy ? 'Saving…' : 'Continue'}</button>
        </form>
        <button type="button" class="checkout-switch" data-as-guest>Book under different details</button>`;
    } else {
      const tab = (k: CheckoutMode, label: string) =>
        `<button type="button" class="button button-sm ${k === mode ? 'button-primary' : 'button-default-outline'}" data-mode="${k}">${label}</button>`;
      const panel =
        mode === 'signin'
          ? `<form data-checkout="signin">
              ${field('bookEmail', 'Email', 'email', { autocomplete: 'email' })}
              ${field('bookPassword', 'Password', 'password', { autocomplete: 'current-password' })}
              <button class="button button-primary button-block" type="submit"${busy ? ' disabled' : ''}>${busy ? 'Signing in…' : 'Sign in and continue'}</button>
              <p class="member-hint">Your Secrets Hideaway account works here. Signing in keeps this booking with the rest of your stays.</p>
            </form>`
          : `<form data-checkout="guest">
              <div class="checkout-row">
                ${field('guestFirstName', 'First name', 'text', { autocomplete: 'given-name' })}
                ${field('guestLastName', 'Last name', 'text', { autocomplete: 'family-name' })}
              </div>
              ${field('guestEmail', 'Email', 'email', { autocomplete: 'email' })}
              ${field('guestPhone', 'Phone number (optional)', 'tel', { autocomplete: 'tel', required: false })}
              <label class="checkout-optin">
                <input type="checkbox" id="guestCreateAccount"/>
                <span>Create an account so I can manage this booking later</span>
              </label>
              <div class="checkout-account" hidden>
                ${field('guestUsername', 'Username', 'text', { autocomplete: 'username', required: false })}
                ${field('guestNewPassword', 'Password', 'password', { autocomplete: 'new-password', required: false })}
              </div>
              <button class="button button-primary button-block" type="submit"${busy ? ' disabled' : ''}>${busy ? 'Saving…' : 'Continue'}</button>
            </form>`;
      body = `<div class="group-sm checkout-tabs">${tab('signin', 'I have an account')}${tab('guest', 'Continue as guest')}</div>${panel}`;
    }

    el.innerHTML = `<div class="container">
      <div class="row justify-content-center"><div class="col-12 col-lg-10 col-xl-8">
        <div class="member-card checkout-card">
          <button type="button" class="checkout-back" data-back>&larr; Back to results</button>
          <h3>${identified ? 'One more step' : 'Almost there'}</h3>
          ${checkoutSummary(booking)}
          ${noticeHtml}
          ${body}
        </div>
      </div></div>
    </div>`;

    el.querySelector<HTMLButtonElement>('[data-back]')?.addEventListener('click', back);
    el.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
      b.addEventListener('click', () => {
        mode = b.dataset.mode as CheckoutMode;
        notice = null;
        draw();
      }),
    );

    // The account we hold may carry an address they cannot be reached on, and
    // upstream hands back a placeholder for deleted members. Either way they
    // need a way past their own session.
    el.querySelector<HTMLButtonElement>('[data-as-guest]')?.addEventListener('click', () => {
      asGuest = true;
      mode = 'guest';
      notice = null;
      draw();
    });

    const optIn = el.querySelector<HTMLInputElement>('#guestCreateAccount');
    const account = el.querySelector<HTMLElement>('.checkout-account');
    optIn?.addEventListener('change', () => {
      if (account) account.hidden = !optIn.checked;
      // Only demanded once they have asked for an account, so a guest is never
      // blocked by fields they cannot even see.
      account?.querySelectorAll('input').forEach((i) => {
        i.required = optIn.checked;
      });
    });

    el.querySelector<HTMLFormElement>('[data-checkout]')?.addEventListener('submit', (e) => {
      e.preventDefault();
      void submit((e.currentTarget as HTMLFormElement).dataset.checkout as string);
    });

    restore();
  };

  const value = (id: string): string =>
    el.querySelector<HTMLInputElement>(`#${id}`)?.value.trim() ?? '';

  const submit = async (kind: string): Promise<void> => {
    busy = true;
    notice = null;
    draw();
    try {
      if (kind === 'signin') {
        await deps.memberLogin(value('bookEmail'), value('bookPassword'));
        // Read the profile back rather than trusting the form: the account may
        // carry a different name than whatever was typed to sign in.
        const me = await api.member.me();
        await api.bookings.identify(booking.id, {
          email: me.email,
          firstName: me.firstName || me.username,
          lastName: me.lastName || me.username,
        });
      } else if (kind === 'member') {
        const me = deps.getMember();
        if (!me) throw new Error('no member');
        const phone = value('bookPhone');
        await api.bookings.identify(booking.id, {
          email: me.email,
          firstName: me.firstName || me.username,
          lastName: me.lastName || me.username,
          ...(phone ? { phone } : {}),
        });
      } else {
        const identity = {
          email: value('guestEmail'),
          firstName: value('guestFirstName'),
          lastName: value('guestLastName'),
          ...(value('guestPhone') ? { phone: value('guestPhone') } : {}),
        };
        // Registering first means the booking links to the new account rather
        // than being filed as a guest booking that happens to share an email.
        if (el.querySelector<HTMLInputElement>('#guestCreateAccount')?.checked) {
          const { pendingReason } = await deps.memberRegister({
            firstName: identity.firstName,
            lastName: identity.lastName,
            email: identity.email,
            username: value('guestUsername') || identity.email.split('@')[0] || identity.email,
            password: value('guestNewPassword'),
          });
          // An account that still needs verifying or approving comes back with
          // no session. Say so, rather than leaving them to find out later that
          // they cannot sign in.
          accountNote = pendingReason;
        }
        await api.bookings.identify(booking.id, identity);
      }
      identified = true;
      // Hold the room straight away: the payment form cannot be drawn without
      // a live intent, and the visitor has nothing to do in between.
      void holdRoomAndMountForm();
    } catch (err) {
      notice = { kind: 'error', text: errorText(err, 'We could not save your details. Please try again.') };
    } finally {
      busy = false;
      draw();
    }
  };

  const holdRoomAndMountForm = async (): Promise<void> => {
    busy = true;
    paymentError = null;
    draw();
    try {
      const session = await api.bookings.payment(booking.id);
      // The room we hold is not always the room they picked: about one offer in
      // six is already sold, and we fall back to another at the same hotel.
      // That can change the price, so show what is actually about to be charged
      // rather than leaving the old figure on screen above the card form.
      if (
        session.totalPriceCents !== booking.totalPriceCents ||
        session.roomType !== booking.roomType
      ) {
        const was = formatMoney(booking.totalPriceCents, booking.currency);
        const now = formatMoney(session.totalPriceCents, session.currency);
        priceChange =
          session.totalPriceCents === booking.totalPriceCents
            ? `The room you picked has just gone, so we have held ${session.roomType} instead, at the same price.`
            : `The room you picked has just gone. We have held ${session.roomType} instead, and the total is now ${now} rather than ${was}.`;
        booking = {
          ...booking,
          roomType: session.roomType,
          totalPriceCents: session.totalPriceCents,
          currency: session.currency,
          nightlyPriceCents: Math.round(session.totalPriceCents / Math.max(1, booking.nights)),
        };
      }
      busy = false;
      draw();
      await mountPaymentForm(session);
    } catch (err) {
      busy = false;
      paymentError = errorText(err, 'We could not hold that room. Please try again.');
      draw();
    }
  };

  draw();
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Record the chosen room, then hand over to the checkout. */
async function startCheckout(
  el: HTMLElement,
  offerId: string,
  criteria: BookingCriteria,
  deps: CheckoutDeps,
  back: () => void,
): Promise<void> {
  el.innerHTML = message('Holding that room…');
  try {
    const booking = await api.bookings.start({ offerId, ...criteria });
    renderCheckout(el, booking, deps, back);
  } catch (err) {
    el.innerHTML = `${message(errorText(err, 'We could not start that booking. Please try again.'))}
      <div class="text-center offset-top-20"><button type="button" class="button button-default-outline button-sm" data-back>Back to results</button></div>`;
    el.querySelector<HTMLButtonElement>('[data-back]')?.addEventListener('click', back);
  }
}

/** The search behind a set of results, needed again to record a booking. */
interface BookingCriteria {
  radiusKm: number;
  checkIn: string;
  checkOut: string;
  guests: number;
}

// Render the results into `el` with a stay summary + sort controls that
// re-render on click (offers are injected HTML, so we rewire after each render).
function renderResults(
  el: HTMLElement,
  res: { offers: RoomOffer[]; cached: boolean },
  summary: string,
  criteria: BookingCriteria,
  deps: CheckoutDeps,
): void {
  res.offers.forEach((o, i) => offerKeys.set(o, String(i)));
  const groups = groupByHotel(res.offers);
  const rooms = res.offers.length;
  const render = (key: SortKey): void => {
    const sorted = sortGroups(groups, key);
    const btn = (k: SortKey, label: string) =>
      `<button type="button" class="button button-sm ${k === key ? 'button-primary' : 'button-default-outline'}" data-sort="${k}">${label}</button>`;
    el.innerHTML = `<div class="container container-wide">
      <div class="row justify-content-center"><div class="col-12 text-center">
        <h3>${groups.length} hotel${groups.length === 1 ? '' : 's'} near Secrets Hideaway</h3>
        <p class="po-muted">${rooms} room${rooms === 1 ? '' : 's'} available &middot; ${escapeHtml(summary)}${res.cached ? ' &middot; cached' : ''}</p>
        <div class="divider divider-default"></div>
        <p class="fs-sm po-muted" style="margin-bottom:8px">Sort by</p>
        <div class="group-sm">${btn('distance', 'Distance')}${btn('price', 'Price')}${btn('rating', 'Rating')}</div>
      </div></div>
      <div class="row row-50 offset-top-30">${sorted.map(hotelGroupCard).join('')}</div>
    </div>`;
    el.querySelectorAll<HTMLButtonElement>('[data-sort]').forEach((b) =>
      b.addEventListener('click', () => render(b.dataset.sort as SortKey)),
    );
    // Reserving takes over the results panel. Going back re-renders on the sort
    // the visitor had chosen, so they land where they left.
    el.querySelectorAll<HTMLButtonElement>('[data-book]').forEach((b) =>
      b.addEventListener('click', () => {
        const offer = res.offers[Number(b.dataset.book)];
        if (offer) void startCheckout(el, offer.id, criteria, deps, () => render(key));
      }),
    );
  };
  render('distance');
}

// A room search is not quick — the vendor is paged for hotels near the resort and
// then priced per hotel — and a single centred line of text made a page that was
// working look like a page that had stopped. These placeholders are shaped like
// real results, so the wait reads as work and the layout does not jump when the
// offers land on top of them.
const SEARCH_SKELETON_COUNT = 4;

// Vary the bar widths so four cards don't read as one repeating pattern.
const SKELETON_WIDTHS = [
  { title: 70, meta: 50, room: 65 },
  { title: 58, meta: 62, room: 52 },
  { title: 76, meta: 44, room: 71 },
  { title: 64, meta: 55, room: 60 },
];

function hotelResultSkeletonCard(i: number): string {
  const { title, meta, room } = SKELETON_WIDTHS[i % SKELETON_WIDTHS.length] ?? {
    title: 70,
    meta: 50,
    room: 65,
  };
  return `<div class="col-12 col-xl-6">
    <article class="hotel-result" aria-hidden="true">
      <div class="so-skel" style="width:100%;aspect-ratio:21/9;border-radius:0"></div>
      <div class="hotel-result-body">
        <div class="so-skel" style="height:22px;width:${title}%;margin-bottom:10px"></div>
        <div class="so-skel" style="height:12px;width:${meta}%"></div>
        <ul class="room-list">
          <li class="room-row">
            <div>
              <div class="so-skel" style="height:14px;width:${room}%;margin-bottom:8px"></div>
              <div class="so-skel" style="height:11px;width:${Math.round(room * 0.7)}%"></div>
            </div>
            <div class="so-skel" style="height:18px;width:64px"></div>
            <div class="so-skel" style="height:38px;width:96px;border-radius:19px"></div>
          </li>
        </ul>
      </div>
    </article>
  </div>`;
}

/**
 * The loading state for a room search, mirroring `renderResults`' own structure
 * so the heading and grid stay where they are when real offers arrive.
 *
 * The wording is kept rather than replaced by bare shapes: the cards are
 * decorative and hidden from assistive tech, so this line is what a screen
 * reader announces, and it is the only thing that says why the page is busy.
 * Owns the stylesheet call because both callers would otherwise have to
 * remember it.
 */
function searchSkeleton(text: string): string {
  ensureSkeletonStyle();
  const cards = Array.from({ length: SEARCH_SKELETON_COUNT }, (_, i) =>
    hotelResultSkeletonCard(i),
  ).join('');
  return `<div class="container container-wide" role="status" aria-live="polite">
    <div class="row justify-content-center"><div class="col-12 text-center">
      <h3>${escapeHtml(text)}</h3>
      <p class="po-muted">Checking live prices at hotels near Secrets Hideaway</p>
      <div class="divider divider-default"></div>
    </div></div>
    <div class="row row-50 offset-top-30">${cards}</div>
  </div>`;
}

/**
 * Move the visitor to the results the moment they ask for them.
 *
 * Deliberately not a bare `scrollIntoView`. Two things go wrong with that:
 * the target is measured before the browser has laid out the skeleton we just
 * injected, and a smooth scroll can be dropped or interrupted outright — which
 * leaves someone who just pressed a button watching a page that does nothing
 * for several seconds, then jumps when the offers land. That is the exact
 * complaint this exists to prevent.
 *
 * So: wait one frame for layout, ask for a smooth scroll, and if the page still
 * has not moved shortly after, stop being polite and jump.
 */
function revealResults(el: HTMLElement): void {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const startedAt = window.scrollY;
  // Called straight away rather than inside requestAnimationFrame: scrollIntoView
  // flushes layout itself, so the skeleton we just injected is already measured,
  // and rAF does not fire at all in a hidden tab — which would mean no scroll and
  // no fallback either.
  el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  if (reduced) return;
  window.setTimeout(() => {
    // Under 40px of movement means the smooth scroll never took, not that it is
    // still easing — by 500ms it would be most of the way there.
    if (Math.abs(window.scrollY - startedAt) < 40) {
      window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().top, behavior: 'auto' });
    }
  }, 500);
}

// A one-line summary of the searched stay for the results header.
function staySummary(checkIn: string, checkOut: string, guests: number): string {
  const nights = Math.max(1, Math.round((Date.parse(checkOut) - Date.parse(checkIn)) / 86_400_000));
  const fmt = (iso: string) => formatDate(iso) || iso;
  return `${fmt(checkIn)} → ${fmt(checkOut)} · ${nights} night${nights === 1 ? '' : 's'} · ${guests} guest${guests === 1 ? '' : 's'}`;
}

function ensureResults(root: HTMLElement): HTMLElement {
  let el = root.querySelector<HTMLElement>('#booking-results');
  if (!el) {
    el = document.createElement('section');
    el.id = 'booking-results';
    el.className = 'section section-md bg-default';
    // Prefer just below the home hero; otherwise render above the footer.
    const hero = root.querySelector('.swiper-form-wrap')?.closest('section');
    const footer = root.querySelector('.page-footer');
    if (hero) hero.after(el);
    else if (footer?.parentElement) footer.parentElement.insertBefore(el, footer);
    else root.appendChild(el);
  }
  return el;
}

interface StayCriteria {
  checkIn: string | null;
  checkOut: string | null;
  guests: number;
}

function readStayForm(root: HTMLElement): StayCriteria {
  const value = (id: string) => root.querySelector<HTMLInputElement>(`#${id}`)?.value ?? '';
  return {
    checkIn: toIsoDate(value('forms-current-from')),
    checkOut: toIsoDate(value('forms-current-to')),
    // Adults only, so the single stepper is the whole guest count.
    guests: Math.max(1, Number.parseInt(value('form-element-stepper') || '2', 10) || 2),
  };
}

/**
 * Give the stay form sensible dates and keep the range valid.
 *
 * The template's pickers start empty and only forbid past dates, so a visitor
 * could land on an empty form or pick a check-out before check-in. Default
 * check-in to today and check-out to tomorrow — but never over a date the /book
 * page prefilled from the query string — and keep check-out at least a night
 * after check-in.
 *
 * The input value is the single source of truth: the search reads it (via
 * toIsoDate) and the theme picker re-reads the field when it opens. We
 * deliberately do NOT call the picker's setDate/setMinDate methods — those names
 * aren't part of this plugin build, and invoking them re-initialises it with a
 * broken config, which is what showed "Invalid date" in the calendar.
 */
function setupStayDates(root: HTMLElement): void {
  const from = root.querySelector<HTMLInputElement>('#forms-current-from');
  const to = root.querySelector<HTMLInputElement>('#forms-current-to');
  if (!from || !to) return;

  const DAY_MS = 86_400_000;
  // jQuery only, to hear the theme picker's change event.
  const jq = (
    window as unknown as { jQuery?: (el: Element) => { on(event: string, handler: () => void): void } }
  ).jQuery;

  // The picker's own display format ('dddd DD MMMM YYYY'), so a defaulted value
  // reads the same as a picked one and toIsoDate parses it back.
  const display = (d: Date): string => {
    const weekday = d.toLocaleDateString('en-GB', { weekday: 'long' });
    const month = d.toLocaleDateString('en-GB', { month: 'long' });
    return `${weekday} ${String(d.getDate()).padStart(2, '0')} ${month} ${d.getFullYear()}`;
  };
  const dateOf = (input: HTMLInputElement): Date | null => {
    const iso = toIsoDate(input.value);
    return iso ? new Date(`${iso}T00:00:00`) : null;
  };
  const setDate = (input: HTMLInputElement, d: Date): void => {
    input.value = display(d);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Resolve both dates: honor existing values (the /book query-string prefill),
  // otherwise default check-in to today and check-out to the night after. Write
  // both back in the picker's display format, so a query-string ISO date reads
  // like a picked one.
  const checkIn = dateOf(from) ?? today;
  let checkOut = dateOf(to) ?? new Date(checkIn.getTime() + DAY_MS);
  if (checkOut.getTime() <= checkIn.getTime()) checkOut = new Date(checkIn.getTime() + DAY_MS);
  setDate(from, checkIn);
  setDate(to, checkOut);

  // Check-out stays at least a night after check-in: snap it forward whenever a
  // new check-in lands on or after the current check-out.
  const enforce = (): void => {
    const ci = dateOf(from);
    if (!ci) return;
    const co = dateOf(to);
    if (!co || co.getTime() <= ci.getTime()) setDate(to, new Date(ci.getTime() + DAY_MS));
  };
  enforce();
  jq?.(from).on('change', enforce);
}

// A tight overflow radius on purpose: guests want a room near the resort, not
// one across Orlando. It also keeps the hotel search to a page or two, well
// under LiteAPI's rate limit — a 40km net paged ~20 times and tripped a 429.
// ~9 miles still covers every neighbour the resort refers guests to (the
// farthest, Hunter Arms, is 6mi) and matches the featured showcase's radius.
const SEARCH_RADIUS_KM = 15;

async function runSearch(
  root: HTMLElement,
  { checkIn, checkOut, guests }: StayCriteria,
  { scroll }: { scroll: boolean },
  deps: CheckoutDeps,
): Promise<void> {
  const results = ensureResults(root);
  if (!checkIn || !checkOut) {
    results.innerHTML = message('Please choose your check-in and check-out dates.');
    return;
  }
  // Belt-and-suspenders: the picker keeps check-out after check-in, but a bad
  // pair can still arrive via the query string, so don't send it to the vendor.
  if (Date.parse(checkOut) <= Date.parse(checkIn)) {
    results.innerHTML = message('Your check-out date must be after your check-in date.');
    return;
  }
  results.innerHTML = searchSkeleton('Searching for available rooms…');
  // Straight after the skeleton, not once the offers land. The search takes
  // several seconds, and a loading state nobody has scrolled to is no better
  // than no loading state at all. Waiting also meant the page sat still after a
  // click and then jumped on its own — and it would yank back anyone who had
  // scrolled off during the wait. The skeleton gives the section its full height
  // first, so this lands where the results themselves will be.
  if (scroll) revealResults(results);
  const criteria: BookingCriteria = { radiusKm: SEARCH_RADIUS_KM, checkIn, checkOut, guests };
  try {
    const res = await api.availability.search(criteria);
    if (res.offers.length) {
      renderResults(results, res, staySummary(checkIn, checkOut, guests), criteria, deps);
    } else {
      results.innerHTML = message('No rooms available for those dates. Try adjusting them.');
    }
  } catch {
    results.innerHTML = message('Sorry, we could not load availability right now. Please try again.');
  }
}

/**
 * The home page hands the search off to /book rather than answering in place, so
 * a search has an address of its own and can be linked to or reloaded.
 */
function wireBookingHandoff(
  form: HTMLFormElement,
  root: HTMLElement,
  navigate: (to: string) => void,
): void {
  setupStayDates(root);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const { checkIn, checkOut, guests } = readStayForm(root);
    const params = new URLSearchParams({ guests: String(guests) });
    if (checkIn) params.set('checkIn', checkIn);
    if (checkOut) params.set('checkOut', checkOut);
    navigate(`/book?${params.toString()}`);
  });
}

/**
 * Wire the template's own sign-in form.
 *
 * The markup is the template's, floating labels and all, so this only attaches
 * behaviour: no fields are moved and no classes are added.
 */
function wireLoginPage(root: HTMLElement, deps: CheckoutDeps, navigate: (to: string) => void): void {
  const form = root.querySelector<HTMLFormElement>('.rd-mailform');
  if (!form) return;
  const email = form.querySelector<HTMLInputElement>('#forms-login-name');
  const password = form.querySelector<HTMLInputElement>('#forms-login-password');
  if (!email || !password) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void (async () => {
      showFormError(form, null);
      const button = form.querySelector('button[type="submit"]');
      button?.setAttribute('disabled', 'disabled');
      try {
        await deps.memberLogin(email.value.trim(), password.value);
        navigate('/account');
      } catch (err) {
        showFormError(form, errorText(err, 'We could not sign you in. Please try again.'));
      } finally {
        button?.removeAttribute('disabled');
      }
    })();
  });
}

/** Wire the template's own registration form. */
function wireRegisterPage(
  root: HTMLElement,
  deps: CheckoutDeps,
  navigate: (to: string) => void,
): void {
  const form = root.querySelector<HTMLFormElement>('.rd-mailform');
  if (!form) return;
  const val = (id: string) => form.querySelector<HTMLInputElement>(`#${id}`)?.value.trim() ?? '';

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void (async () => {
      showFormError(form, null);
      const password = val('form-1-password');
      if (password !== val('form-1-password-confirm')) {
        showFormError(form, 'Those passwords do not match.');
        return;
      }
      const button = form.querySelector('button[type="submit"]');
      button?.setAttribute('disabled', 'disabled');
      try {
        const { pendingReason } = await deps.memberRegister({
          firstName: val('forms-register-first'),
          lastName: val('forms-register-last'),
          email: val('form-1-email'),
          username: val('form-1-username'),
          password,
        });
        if (pendingReason) showFormError(form, `Account created. ${pendingReason}`);
        else navigate('/account');
      } catch (err) {
        showFormError(form, errorText(err, 'We could not create that account.'));
      } finally {
        button?.removeAttribute('disabled');
      }
    })();
  });
}

/** Put a message above the form's submit button, or clear it. */
function showFormError(form: HTMLFormElement, text: string | null): void {
  let box = form.querySelector<HTMLElement>('.form-error-notice');
  if (!text) {
    box?.remove();
    return;
  }
  if (!box) {
    box = document.createElement('div');
    box.className = 'member-notice member-notice-error form-error-notice';
    form.querySelector('.form-button')?.before(box);
  }
  box.textContent = text;
}

/**
 * Returning from the vendor's payment page. The room is paid for; all that is
 * left is to ask our API to place the booking and show the confirmation.
 */
async function resumeAfterPayment(root: HTMLElement, bookingId: string): Promise<void> {
  // The confirmation has to be the first thing on the page, not appended under
  // the search form. Coming back from the payment provider is a full page load,
  // so the scroll restorer puts the visitor at the top: anything below the fold
  // is simply not seen, and a paid booking looks like nothing happened.
  const banner = root.querySelector('.breadcrumbs-custom');
  const panel = document.createElement('section');
  panel.id = 'booking-confirmation';
  panel.className = 'section section-md bg-default';
  if (banner) banner.after(panel);
  else root.prepend(panel);

  // Hide the search form while the booking is being finished. Offering someone
  // a fresh search before they have been told what happened to the last one is
  // how you get a second, accidental booking.
  const search = root.querySelector('.form-request')?.closest('section');
  if (search && search !== panel) search.setAttribute('hidden', 'hidden');

  const card = (heading: string, body: string) =>
    `<div class="container"><div class="row justify-content-center"><div class="col-12 col-lg-10 col-xl-8">
      <div class="member-card checkout-card"><h3>${heading}</h3>${body}</div>
    </div></div></div>`;

  panel.innerHTML = message('Confirming your booking…');
  try {
    const confirmation = await api.bookings.confirm(bookingId);
    panel.innerHTML = card('Booking confirmed', confirmedPanel(confirmation));
  } catch (err) {
    // The vendor refusing the booking after payment (`rate_expired`) reads to
    // the guest as the site eating their money, so name what happened rather
    // than leaving them with a generic failure. Everything else keeps the
    // deliberately vague wording, because we genuinely do not know what broke.
    const expired = err instanceof ApiClientError && err.code === 'rate_expired';
    panel.innerHTML = card(
      expired ? 'That room was taken before we could book it' : 'We could not finish that booking',
      `<div class="member-notice member-notice-error">${escapeHtml(errorText(err, 'Something went wrong after your payment.'))}</div>
       <p class="member-hint">Your payment may already have gone through, so please do not pay again. Call 844-563-6969 with this reference and we will sort it out: <strong>${escapeHtml(bookingId)}</strong></p>`,
    );
  }
}

/** The booking page answers in place, and prefills from the query string. */
function wireBookingPage(form: HTMLFormElement, root: HTMLElement, deps: CheckoutDeps): void {
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const criteria = readStayForm(root);
    const params = new URLSearchParams({ guests: String(criteria.guests) });
    if (criteria.checkIn) params.set('checkIn', criteria.checkIn);
    if (criteria.checkOut) params.set('checkOut', criteria.checkOut);
    // Keep the URL in step with what's on screen without remounting the page.
    window.history.replaceState(null, '', `/book?${params.toString()}`);
    void runSearch(root, criteria, { scroll: true }, deps);
  });

  const params = new URLSearchParams(window.location.search);

  // Back from the payment form. Finish the booking rather than re-running a
  // search the visitor has already moved past.
  const returning = params.get('booking');
  if (returning) {
    void resumeAfterPayment(root, returning);
    return;
  }

  const checkIn = params.get('checkIn');
  const checkOut = params.get('checkOut');
  const guests = Math.max(1, Number.parseInt(params.get('guests') ?? '2', 10) || 2);

  const set = (id: string, v: string | null) => {
    const input = root.querySelector<HTMLInputElement>(`#${id}`);
    if (input && v) input.value = v;
  };
  set('forms-current-from', checkIn);
  set('forms-current-to', checkOut);
  set('form-element-stepper', String(guests));

  // Arriving with dates already chosen means the visitor has asked a question;
  // answer it instead of making them press the button again.
  if (checkIn && checkOut) {
    void runSearch(root, { checkIn, checkOut, guests }, { scroll: false }, deps);
  }

  // Defaults + the check-out-after-check-in rule, after the prefill so any
  // query-string dates win over the defaults.
  setupStayDates(root);
}

// --- Inject admin-managed content into the template markup ------------------
function slideMarkup(s: Slide): string {
  const cta =
    s.ctaLabel && s.ctaHref
      ? `<a class="button button-default-outline button-sm" href="${escapeHtml(s.ctaHref)}">${escapeHtml(s.ctaLabel)}</a>`
      : '';
  return `<div class="swiper-slide" data-slide-bg="${escapeHtml(s.imageUrl)}"><div class="swiper-slide-caption"><div class="container container-bigger swiper-main-section"><div class="row row-fix justify-content-sm-center justify-content-md-start"><div class="col-md-6 col-lg-5 col-xl-4 col-xxl-5"><h3>${escapeHtml(s.heading)}</h3><div class="divider divider-default"></div>${s.body ? `<p class="text-spacing-sm">${escapeHtml(s.body)}</p>` : ''}${cta}</div></div></div></div></div>`;
}

// The full set of photos lives on SecretsFL, so a tile opens that gallery in a
// new tab rather than trapping the visitor in a lightbox over one image.
function galleryMarkup(g: SecretsGalleryImage): string {
  const caption = g.title
    ? `<div class="caption"><p class="caption-title">${escapeHtml(g.title)}</p></div>`
    : '';
  const href = g.sourceUrl ?? 'https://www.secretsfl.com/galleries';
  return `<div class="col-12 col-md-6 col-lg-4 isotope-item"><div class="gallery-item-wrap"><a class="gallery-item" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer"><div class="gallery-item-image"><figure><img src="${escapeHtml(g.thumbnailUrl || g.imageUrl)}" alt="${escapeHtml(g.title ?? '')}" width="570" height="380"/></figure>${caption}</div></a></div></div>`;
}

// A news card in the template's post-blog style. `wrap` adds the grid column for
// the News page; the home "Latest News" owl carousel takes bare articles.
// A bare YYYY-MM-DD parses as UTC midnight, which renders as the day before in
// US time zones, so date-only values are built in local time instead.
function parseDate(iso: string | null): Date | null {
  if (!iso) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = parts
    ? new Date(Number(parts[1] ?? 0), Number(parts[2] ?? 1) - 1, Number(parts[3] ?? 1))
    : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(iso: string | null): string {
  const d = parseDate(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

// Upstream sends article bodies as HTML. Cards want a plain-text teaser long
// enough to fill the box, cut on a word boundary. DOMParser keeps the markup
// inert while we pull the text out of it.
function textTeaser(html: string | null, limit: number): string {
  if (!html) return '';
  const text = (new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const space = cut.lastIndexOf(' ');
  return `${(space > limit * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:!?]+$/, '')}…`;
}

// A short excerpt leaves the card half empty, so fall back to the body copy.
function cardTeaser(excerpt: string | null, contentHtml: string | null, limit: number): string {
  const trimmed = (excerpt ?? '').trim();
  if (trimmed.length >= limit * 0.5) return textTeaser(trimmed, limit);
  return textTeaser(contentHtml, limit) || trimmed;
}

// SecretsFL's own placeholder, so a post with no photo still fills its card.
const PLACEHOLDER_IMAGE = 'https://www.secretsfl.com/resources/images/no-image-temp.png';

const dateChip = (text: string): string =>
  text ? `<span class="post-date-chip">${escapeHtml(text)}</span>` : '';

function newsArticleMarkup(a: SecretsNewsArticle): string {
  const href = a.sourceUrl ?? '#';
  const img = a.thumbnailUrl || a.imageUrl || PLACEHOLDER_IMAGE;
  const teaser = cardTeaser(a.excerpt, a.contentHtml, 190);
  const image = `<a class="post-blog-image" href="${escapeHtml(href)}">${dateChip(formatDate(a.publishedDate))}<img src="${escapeHtml(img)}" alt="${escapeHtml(a.title)}" width="570" height="415"/></a>`;
  return `<article class="post-blog">${image}<div class="post-blog-caption"><div class="post-blog-caption-body"><h5><a class="post-blog-title" href="${escapeHtml(href)}">${escapeHtml(a.title)}</a></h5>${teaser ? `<p>${escapeHtml(teaser)}</p>` : ''}</div><div class="post-blog-caption-footer post-blog-action"><a class="button button-primary" href="${escapeHtml(href)}">Read the full story</a></div></div></article>`;
}

const newsCardMarkup = (a: SecretsNewsArticle): string =>
  `<div class="col-md-6 col-xl-4 post-blog-col">${newsArticleMarkup(a)}</div>`;

// An event card in the template's post-blog style, with a date badge and a
// button that runs an overflow-room search for the event's date range. The
// dates are carried on data-* attributes for the click handler.
// Dates the way an American reads them: "Aug 8, 2026", "Aug 8 - 10, 2026",
// "Aug 30 - Sep 1, 2026". Upstream's own string is the fallback.
function eventDateText(e: SecretsEvent): string {
  const start = parseDate(e.startDate);
  const end = parseDate(e.endDate);
  if (!start) return e.dateText ?? '';
  // An event running right now says so, rather than making people work out that
  // today falls inside the range.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (start <= today && today <= (end ?? start)) return 'Happening Now';
  const monthDay = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const full = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (!end || end.getTime() === start.getTime()) return full(start);
  if (start.getFullYear() !== end.getFullYear()) return `${full(start)} - ${full(end)}`;
  const tail = start.getMonth() === end.getMonth() ? end.getDate() : monthDay(end);
  return `${monthDay(start)} - ${tail}, ${end.getFullYear()}`;
}

function eventCardMarkup(e: SecretsEvent): string {
  const href = e.sourceUrl ?? '#';
  const img = e.thumbnailUrl || e.imageUrl || PLACEHOLDER_IMAGE;
  const dates = eventDateText(e);
  const teaser = cardTeaser(e.description, e.contentHtml, 190);
  const image = `<a class="post-blog-image" href="${escapeHtml(href)}">${dateChip(dates)}<img src="${escapeHtml(img)}" alt="${escapeHtml(e.title)}" width="570" height="415"/></a>`;
  return `<div class="col-md-6 col-xl-4 post-blog-col"><article class="post-blog" data-event-start="${escapeHtml(e.startDate ?? '')}" data-event-end="${escapeHtml(e.endDate ?? e.startDate ?? '')}">${image}<div class="post-blog-caption"><div class="post-blog-caption-body"><h5><a class="post-blog-title" href="${escapeHtml(href)}">${escapeHtml(e.title)}</a></h5>${teaser ? `<p>${escapeHtml(teaser)}</p>` : ''}</div><div class="post-blog-caption-footer post-blog-action"><button class="button button-primary event-find-rooms" type="button">Book a room for these dates</button></div></div></article></div>`;
}

// Add a day to a YYYY-MM-DD string (for single-day events → 1-night stay).
function nextDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

// Wire each event card's "Find a room" button to an availability search over
// that event's date range, rendering offers into a shared results section.
function wireEventCards(root: HTMLElement, deps: CheckoutDeps): void {
  root.querySelectorAll<HTMLButtonElement>('.event-find-rooms').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const article = btn.closest<HTMLElement>('.post-blog');
      const start = (article?.dataset.eventStart ?? '').slice(0, 10);
      const endRaw = (article?.dataset.eventEnd ?? '').slice(0, 10);
      const results = ensureResults(root);
      if (!start) {
        results.innerHTML = message('No dates are set for this event yet.');
        return;
      }
      const checkOut = endRaw && endRaw > start ? endRaw : nextDay(start);
      results.innerHTML = searchSkeleton('Searching for rooms for those dates…');
      revealResults(results);
      try {
        const criteria: BookingCriteria = {
          radiusKm: SEARCH_RADIUS_KM,
          checkIn: start,
          checkOut,
          guests: 2,
        };
        const res = await api.availability.search(criteria);
        if (res.offers.length) {
          renderResults(results, res, staySummary(start, checkOut, 2), criteria, deps);
        } else {
          results.innerHTML = message(
            'No rooms available for those dates. Try adjusting them on the home page.',
          );
        }
      } catch {
        results.innerHTML = message('Sorry, we could not load availability right now. Please try again.');
      }
    });
  });
}

function faqMarkup(f: Faq, i: number): string {
  const n = i + 1;
  const open = i === 0;
  // Same light-card shape the generated FAQs use, so admin entries match.
  return `<div class="card card-custom card-light"><div class="card-heading" id="accordion1Heading${n}" role="tab"><div class="card-title"><a class="${open ? '' : 'collapsed'}" role="button" data-toggle="collapse" data-parent="#accordion1" href="#accordion1Collapse${n}" aria-controls="accordion1Collapse${n}" aria-expanded="${open}">${escapeHtml(f.question)}<div class="card-arrow"></div></a></div></div><div class="collapse${open ? ' show' : ''}" id="accordion1Collapse${n}" role="tabpanel" aria-labelledby="accordion1Heading${n}"><div class="card-body"><p>${escapeHtml(f.answer)}</p></div></div></div>`;
}

// --- Featured nearby rooms --------------------------------------------------
// The stay dates, the one-room-per-hotel trim and the caching all happen on the
// API now (GET /v1/availability/featured), so this shows up on more than one
// page without costing a vendor call per view.

function featuredRoomCard(card: FeaturedHotelCard): string {
  const { hotel, offer } = card;
  const place = [hotel.city, hotel.region].filter(Boolean).join(', ');
  const stars = hotel.starRating ? '★'.repeat(Math.max(0, Math.round(hotel.starRating))) : '';
  const facts = [place, `${miles(hotel.distanceKm)} miles from the resort`]
    .filter(Boolean)
    .join(' &middot; ');
  // Sold out for tonight's stay: still show the hotel, but prompt a date search
  // instead of a price so the curated lineup is always complete.
  const price = offer
    ? `from ${formatMoney(offer.nightlyPriceCents, offer.currency)}`
    : '<span class="hotel-card-unavailable">Check dates</span>';
  const secondLine = offer
    ? `${escapeHtml(offer.roomType)}${stars ? ` <span style="color:#ce1417">${stars}</span>` : ''}`
    : `No rooms for tonight — search your dates${stars ? ` <span style="color:#ce1417">${stars}</span>` : ''}`;
  const cta = offer ? 'Search Rooms' : 'Check Dates';
  return `<div class="col-sm-6 col-xl-4">
    <article class="event-default-wrap">
      <div class="event-default">
        <figure class="event-default-image"><img src="${escapeHtml(hotel.thumbnailUrl || '/theme/images/home-01-570x370.jpg')}" alt="${escapeHtml(hotel.name)}" width="570" height="370"/></figure>
        <div class="event-default-caption"><a class="button button-xs button-primary" href="/">${cta}</a></div>
      </div>
      <div class="event-default-inner">
        <h5 class="hotel-card-heading"><span class="event-default-title">${escapeHtml(hotel.name)}</span><span class="event-default-price">${price}</span></h5>
        <p class="fs-sm hotel-card-meta">${secondLine}</p>
        <p class="fs-sm hotel-card-meta">${facts}</p>
      </div>
    </article>
  </div>`;
}

const slotMessage = (text: string): string =>
  `<div class="col-12 text-center"><p class="po-muted">${escapeHtml(text)}</p></div>`;

// A pulsing grey placeholder card, shaped like a real one, so the showcase reads
// as loading — and holds its layout — instead of looking empty while the
// availability call (a second or two) is in flight.
const FEATURED_SKELETON_COUNT = 6;

// Shared by the showcase and the room-search results; injected once per page.
function ensureSkeletonStyle(): void {
  if (document.getElementById('so-skeleton-style')) return;
  const style = document.createElement('style');
  style.id = 'so-skeleton-style';
  style.textContent =
    '@keyframes soSkelPulse{0%,100%{opacity:1}50%{opacity:.5}}' +
    '.so-skel{background:#e4e7eb;border-radius:6px;animation:soSkelPulse 1.2s ease-in-out infinite}' +
    '@media (prefers-reduced-motion:reduce){.so-skel{animation:none}}';
  document.head.appendChild(style);
}

function featuredSkeletonCard(): string {
  return `<div class="col-sm-6 col-xl-4">
    <article class="event-default-wrap" aria-hidden="true">
      <div class="so-skel" style="width:100%;padding-top:65%"></div>
      <div class="event-default-inner">
        <div class="so-skel" style="height:20px;width:75%;margin:16px 0 12px"></div>
        <div class="so-skel" style="height:12px;width:55%;margin-bottom:8px"></div>
        <div class="so-skel" style="height:12px;width:40%"></div>
      </div>
    </article>
  </div>`;
}

// The showcase is the same on every page that shows it, so cache it in the tab:
// a repeat page renders instantly instead of skeleton-then-fetch again, then
// refreshes in the background. sessionStorage, not local — the server figure
// rolls on its own cadence, so a fresh tab starts clean.
const FEATURED_CACHE_KEY = 'so:featured:v1';

function readFeaturedCache(): FeaturedRoomsResponse | null {
  try {
    const raw = sessionStorage.getItem(FEATURED_CACHE_KEY);
    return raw ? (JSON.parse(raw) as FeaturedRoomsResponse) : null;
  } catch {
    return null;
  }
}

function renderFeatured(slot: HTMLElement, res: FeaturedRoomsResponse): void {
  // Prefer the full curated lineup (`hotels`, incl. sold-out cards); fall back
  // to available offers for an older API that predates the `hotels` field.
  const cards: FeaturedHotelCard[] =
    res.hotels ?? res.offers.map((offer) => ({ hotel: offer.hotel, offer }));
  slot.innerHTML = cards.length
    ? cards.map(featuredRoomCard).join('')
    : slotMessage('Nothing is showing as open right now. Try a search with your own dates.');
}

async function patchFeaturedRooms(root: HTMLElement): Promise<void> {
  const slot = root.querySelector<HTMLElement>('#featured-rooms');
  if (!slot) return;

  // Instant from cache on a repeat page; otherwise a skeleton while we fetch.
  const cached = readFeaturedCache();
  if (cached) {
    renderFeatured(slot, cached);
  } else {
    ensureSkeletonStyle();
    slot.innerHTML = Array.from({ length: FEATURED_SKELETON_COUNT }, featuredSkeletonCard).join('');
  }

  try {
    const res = await api.availability.featured();
    try {
      sessionStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify(res));
    } catch {
      /* private mode / quota — the render below still happens */
    }
    renderFeatured(slot, res);
  } catch {
    // Keep whatever the cache already drew; only a cold load shows the error.
    if (!cached) {
      slot.innerHTML = slotMessage(
        "We couldn't load rooms just now. Try a search with your own dates.",
      );
    }
  }
}

// The strip along the bottom of the home page, six gallery shots picked at
// random so it isn't the same six every visit.
function galleryStripMarkup(g: SecretsGalleryImage): string {
  const caption = g.title
    ? `<div class="caption"><p class="caption-title">${escapeHtml(g.title)}</p></div>`
    : '';
  const href = g.sourceUrl ?? 'https://www.secretsfl.com/galleries';
  return `<div class="col-12 col-md-6 col-lg-4 isotope-item"><div class="gallery-item-wrap"><a class="gallery-item gallery-item-fullwidth" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer"><div class="gallery-item-image"><figure><img src="${escapeHtml(g.thumbnailUrl || g.imageUrl)}" alt="${escapeHtml(g.title ?? '')}" width="640" height="430"/></figure>${caption}</div></a></div></div>`;
}

function pickRandom<T>(items: T[], count: number): T[] {
  return items
    .map((item) => ({ item, order: Math.random() }))
    .sort((a, b) => a.order - b.order)
    .slice(0, count)
    .map((entry) => entry.item);
}

// Replace the template's demo content with admin-managed content (before the
// jQuery plugins init). On error/empty, the template's default content stays.
async function patchContent(root: HTMLElement, src: string): Promise<void> {
  try {
    if (src.includes('home.html')) {
      const [slides, news, gallery] = await Promise.all([
        api.content.slides().catch(() => []),
        api.content.news().catch(() => []),
        api.content.gallery().catch(() => []),
      ]);
      const wrap = root.querySelector('.swiper-wrapper');
      if (slides.length && wrap) wrap.innerHTML = slides.map(slideMarkup).join('');
      // Home "Latest News" owl carousel takes the three most recent articles.
      const owl = root.querySelector('.owl-carousel-team');
      if (news.length && owl) owl.innerHTML = news.slice(0, 6).map(newsArticleMarkup).join('');
      const strip = root.querySelector('[data-isotope-group="gallery-02"]');
      if (gallery.length && strip) {
        strip.innerHTML = pickRandom(gallery, 6).map(galleryStripMarkup).join('');
      }
      // Deliberately not awaited: an availability search takes seconds and the
      // page shouldn't sit behind the loader waiting for it.
      void patchFeaturedRooms(root);
    } else if (src.includes('about.html')) {
      void patchFeaturedRooms(root);
    } else if (src.includes('gallery.html')) {
      const items = await api.content.gallery();
      const grid = root.querySelector('.isotope');
      if (items.length && grid) grid.innerHTML = items.map(galleryMarkup).join('');
    } else if (src.includes('events.html')) {
      const events = await api.content.events();
      const row = root.querySelector('.post-blog')?.closest('.row');
      if (events.length && row) row.innerHTML = events.map(eventCardMarkup).join('');
    } else if (src.includes('news.html')) {
      const articles = await api.content.news();
      const row = root.querySelector('.post-blog')?.closest('.row');
      if (articles.length && row) row.innerHTML = articles.map(newsCardMarkup).join('');
    } else if (src.includes('faqs.html')) {
      const list = await api.content.faqs();
      const acc = root.querySelector('#accordion1');
      if (list.length && acc) acc.innerHTML = list.map(faqMarkup).join('');
    }
  } catch {
    // Keep the template's default content.
  }
}

export function TemplatePage({ src }: { src: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { member, memberLogin, memberRegister } = useAuth();

  // Checkout signs people in through the same context the account pages use, so
  // the header and /account agree with what just happened. The member is read
  // through a ref rather than a dependency: signing in mid-checkout must not
  // re-inject the whole template underneath the visitor.
  const memberRef = useRef<MemberProfile | null>(member);
  memberRef.current = member;
  const checkoutRef = useRef<CheckoutDeps>({
    memberLogin,
    memberRegister,
    getMember: () => memberRef.current,
  });
  checkoutRef.current = { memberLogin, memberRegister, getMember: () => memberRef.current };

  useEffect(() => {
    let cancelled = false;
    ensureThemeCss();
    setCanonical(src);
    document.documentElement.classList.add('wide');
    loaderRef.current?.classList.remove('is-hidden');

    const hideLoader = () => loaderRef.current?.classList.add('is-hidden');

    (async () => {
      const html = await fetch(src).then((r) => r.text());
      if (cancelled || !containerRef.current) return;
      containerRef.current.innerHTML = html;

      // These bands are painted by the template's parallax plugin. When it
      // doesn't run, the section is left white and its white text vanishes, so
      // set the image as an ordinary background too.
      containerRef.current.querySelectorAll<HTMLElement>('[data-parallax-img]').forEach((el) => {
        const img = el.dataset.parallaxImg;
        if (!img) return;
        el.style.backgroundImage = `url("${img}")`;
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
      });

      // Swap in admin-managed content before the plugins initialize.
      await patchContent(containerRef.current, src);
      if (cancelled || !containerRef.current) return;

      // jQuery + plugins bundle loads once; the init script re-runs each mount so
      // it initializes against the freshly injected markup.
      const w = window as unknown as { jQuery?: unknown };
      if (!w.jQuery) await loadScript('/theme/js/core.min.js');
      if (cancelled) return;
      await loadScript(`/theme/js/script.js?_=${Date.now()}`);
      if (cancelled || !containerRef.current) return;

      // Client-side routing for internal links; external links open in a new tab.
      // Lightbox anchors point at image files and are driven by lightGallery, so
      // they're left untouched.
      containerRef.current.querySelectorAll('a[href]:not([data-lightgallery])').forEach((el) => {
        const a = el as HTMLAnchorElement;
        const href = a.getAttribute('href') ?? '';
        if (/^https?:\/\//i.test(href)) {
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          return;
        }
        if (href.startsWith('mailto:') || href.startsWith('tel:')) return;
        if (href.startsWith('#')) return;
        if (!isInternalRoute(href)) return;
        a.addEventListener('click', (e) => {
          e.preventDefault();
          navigate(href);
        });
      });

      // The booking widget: the home page hands off to /book, the booking page
      // answers in place.
      const bookingForm = containerRef.current.querySelector<HTMLFormElement>('.form-request form');
      if (bookingForm) {
        if (src.includes('book.html')) {
          wireBookingPage(bookingForm, containerRef.current, checkoutRef.current);
        } else wireBookingHandoff(bookingForm, containerRef.current, navigate);
      }

      // Wire the events page's per-event "Find a room" buttons.
      if (src.includes('events.html')) wireEventCards(containerRef.current, checkoutRef.current);

      // The template's own auth pages: markup from design/site, behaviour here.
      if (src.includes('login.html')) {
        wireLoginPage(containerRef.current, checkoutRef.current, navigate);
      }
      if (src.includes('register.html')) {
        wireRegisterPage(containerRef.current, checkoutRef.current, navigate);
      }

      hideLoader();
    })().catch((err) => {
      console.error('[template] failed to render', src, err);
      hideLoader();
    });

    return () => {
      cancelled = true;
    };
  }, [src, navigate]);

  return (
    <>
      <div ref={loaderRef} className="po-loader" aria-hidden="true">
        <div className="po-spinner" />
      </div>
      <div ref={containerRef} />
    </>
  );
}
